/**
 * La semaine du masteur, « Mes prochains lives » (`tutor/lives`) : ce que `<TutorLivesListPage>` montre,
 * lu dans le store (patron A).
 *
 * Port de l'ancienne modale « VOIR TOUS » (`TutorNextEventsModal`, ouverte par `TutorNextLivesCard`) :
 * ses séances sont celles du compte (`currentUser.live_sessions`, lu par `selectAccount`), une par
 * cours au jour de sa plus proche occurrence, triées comme l'accueil (`toTutorWeekSessions`, l'adaptateur
 * de « Ma journée »). Le view-model les range par jour (« Aujourd'hui », « Demain », « Vendredi 6
 * novembre »).
 *
 * Un `createSelector` sur les tranches qu'il lit (`ports/CLAUDE.md`), le jour compris : « Aujourd'hui »
 * se recalcule passé minuit, à la première action du store.
 */
import moment from "moment"
import { createSelector } from "reselect"

import {
    toTutorWeekSessions,
    type ApiTutorLiveSession,
    type UITutorHomeSession,
} from "../../../adapters/tutor/Home/tutorSessionAdapter"
import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import { type UITutorWeekGroup } from "../../../ui/types/tutorLives"
import { selectAccount } from "../../shared/displayedStudent"
import { groupByDay } from "../../shared/groupByDay"

/** Un jour de la semaine, ses séances gardant ce que leurs gestes lisent (`occurrenceDate`, `substitutionId`). */
export type TutorWeekGroup = UITutorWeekGroup & { sessions: UITutorHomeSession[] }

export type TutorLivesListViewModel = {
    groups: TutorWeekGroup[]
    /** Le compte n'est pas encore là : le squelette. */
    loading: boolean
}

type Account = { live_sessions?: ApiTutorLiveSession[] | null } | null

type ListState = { data?: { staticData?: { school_subjects?: ApiSchoolSubject[] | null } | null } }

const selectLivesList = createSelector(
    [
        // Les séances et la présence du compte, pas le compte : il change d'identité à chaque relecture.
        (state: unknown) => (selectAccount(state as never) as Account)?.live_sessions,
        (state: unknown) => selectAccount(state as never) != null,
        (state: unknown) => (state as ListState)?.data?.staticData?.school_subjects ?? null,
        () => new Date().toDateString(),
    ],
    (liveSessions, accountLoaded, schoolSubjects): TutorLivesListViewModel => ({
        // Dans un jour, l'ordre de l'adaptateur (`sortLiveSessionsCallback` : le jour même, l'écart à
        // maintenant en valeur absolue, qui peut placer un jour avant un autre — `groupByDay` trie les jours).
        groups: groupByDay(toTutorWeekSessions(liveSessions, schoolSubjects, moment())),
        loading: !accountLoaded,
    })
)

export function tutorLivesListViewModel(state: unknown): TutorLivesListViewModel {
    return selectLivesList(state)
}
